import {
  DeliveredProcedureOutlined,
  ScheduleOutlined,
  SnippetsOutlined,
  SwitcherOutlined,
} from '@ant-design/icons';
import { AssignmentIndOutlined, DeleteOutlined, EditOutlined, StarsOutlined } from '@vindr/icons';
import { NewOrder } from '@vindr/services/apis/order.api';
import {
  ORDER_PRIORITY,
  ORDER_STATUS,
  actionStatusMapping,
  statusMappingAction,
} from '@vindr/services/constants/order.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import keycloak from '@vindr/services/keycloak';
import { ContextMenuProps } from '@vindr/ui/src/components/ui/PopupContextMenu/PopupContextMenu';
import isEmpty from 'lodash/isEmpty';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const MENU_TYPE = {
  ASSIGN: 'assign',
  EDIT: 'edit',
  DELETE: 'delete',
  PRIORITY: 'priority',
  NOTE: 'note',
  CHANGE_STATUS: 'change_status',
  CHANGE_DEVICE: 'change_device',
  RECEIVE: 'receive',
  DELIVER: 'deliver',
  RECEIVE_QMS: 'receive_qms',
};

const useContextMenu = ({
  handleContextMenuSelect,
  selectedRowKeys,
}: {
  handleContextMenuSelect: any;
  selectedRowKeys: any;
}) => {
  const { t } = useTranslation('Vindoc');
  const currentPriority = selectedRowKeys?.[0]?.priority;
  const currentStatus = selectedRowKeys?.[0]?.status;

  const canAssign = useCallback((record: NewOrder) => {
    /**
     * Can Assign when:
     *
     * 1. Have permission `PERMISSIONS.order.assign`
     * 2. Order status !== ORDER_STATUS.READING, ORDER_STATUS.SUSPENDED, ORDER_STATUS.CANCEL]
     * 3. Match order = Matched (Have study_id)
     */

    if (isEmpty(record)) return false;

    const hasAssignPermission = keycloak.hasOneOfPerm([PERMISSIONS.order.assign]);
    if (!hasAssignPermission) return false;

    const DISALLOWED_STATUSES = [ORDER_STATUS.READING, ORDER_STATUS.SUSPENDED, ORDER_STATUS.CANCEL];
    const isStatusAllowed = !DISALLOWED_STATUSES.includes(record?.status);
    if (!isStatusAllowed) return false;

    return Boolean(record?.study_id);
  }, []);

  return [
    {
      key: MENU_TYPE.NOTE,
      label: t('Note'),
      icon: <SnippetsOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.NOTE, record),
      disabled: !keycloak.hasOneOfPerm([PERMISSIONS.order.note]),
    },
    {
      key: MENU_TYPE.PRIORITY,
      label: t('Priority'),
      icon: <StarsOutlined className="text-3xl" />,
      disabled: !keycloak.hasOneOfPerm([PERMISSIONS.order.priority]),
      children: Object.keys(ORDER_PRIORITY)
        .filter((key) => isNaN(Number(key)))
        .map((priority) => ({
          key: priority,
          label: t(priority),
          className: priority === ORDER_PRIORITY[currentPriority] ? 'font-bold text-blue-600' : '',
          onClick: (record) =>
            handleContextMenuSelect(MENU_TYPE.PRIORITY, record, ORDER_PRIORITY[priority]),
        })),
    },
    {
      key: MENU_TYPE.CHANGE_STATUS,
      label: t('Change Status'),
      icon: <ScheduleOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.CHANGE_STATUS, record),
      children: statusMappingAction[ORDER_STATUS[currentStatus]]
        ?.map((action: string) => {
          const status = actionStatusMapping[action];
          const isCancelAction = t(action) === t('Cancel');
          const shouldDisableCancel =
            (currentStatus === ORDER_STATUS.IN_PROGRESS ||
              currentStatus === ORDER_STATUS.SCHEDULED) &&
            selectedRowKeys[0]?.study?.study_instance_uid;

          if (isCancelAction && shouldDisableCancel) return null;
          return {
            key: action,
            label: t(action),
            onClick: (record) =>
              handleContextMenuSelect(MENU_TYPE.CHANGE_STATUS, record, null, status),
          };
        })
        .filter(Boolean),
      disabled:
        !keycloak.hasOneOfPerm([PERMISSIONS.order.changestatus]) ||
        [
          ORDER_STATUS.READING,
          ORDER_STATUS.SUBMITTED,
          ORDER_STATUS.APPROVED,
          ORDER_STATUS.COMPLETED,
        ].includes(currentStatus),
    },
    {
      key: MENU_TYPE.ASSIGN,
      label: t('Assign'),
      icon: <AssignmentIndOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.ASSIGN, record),
      disabled: (record) => !canAssign(record),
    },
    {
      key: MENU_TYPE.CHANGE_DEVICE,
      label: t('Change Device'),
      icon: <SwitcherOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.CHANGE_DEVICE, record),
      disabled: !keycloak.hasOneOfPerm([PERMISSIONS.order.update]),
    },
    {
      key: MENU_TYPE.RECEIVE,
      label: t('Receive'),
      icon: <ScheduleOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.RECEIVE, record),
      disabled:
        !keycloak.hasOneOfPerm([PERMISSIONS.order.receive]) ||
        ![ORDER_STATUS.NEW, ORDER_STATUS.CANCEL, ORDER_STATUS.SCHEDULED].includes(currentStatus),
    },
    {
      key: MENU_TYPE.RECEIVE_QMS,
      label: t('Receive QMS'),
      icon: <ScheduleOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.RECEIVE_QMS, record),
      disabled:
        !keycloak.hasOneOfPerm([PERMISSIONS.order.receiveqms]) ||
        ![ORDER_STATUS.NEW, ORDER_STATUS.CANCEL, ORDER_STATUS.SCHEDULED].includes(currentStatus),
    },
    {
      key: MENU_TYPE.DELIVER,
      label: t('Deliver Report'),
      icon: <DeliveredProcedureOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.DELIVER, record),
      disabled:
        !keycloak.hasOneOfPerm([PERMISSIONS.order.deliverreport]) ||
        ![ORDER_STATUS.APPROVED].includes(currentStatus),
    },
    {
      key: MENU_TYPE.EDIT,
      label: t('Edit Order'),
      icon: <EditOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.EDIT, record),
      disabled: !keycloak.hasOneOfPerm([PERMISSIONS.order.update]),
    },
    {
      key: MENU_TYPE.DELETE,
      label: t('Delete'),
      icon: <DeleteOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.DELETE, record),
      disabled:
        !keycloak.hasOneOfPerm([PERMISSIONS.order.delete]) ||
        ![ORDER_STATUS.NEW, ORDER_STATUS.CANCEL].includes(currentStatus),
    },
  ] satisfies ContextMenuProps<any>['menu']['items'];
};

export default useContextMenu;
